import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import eq1 from "../../assets/eq1.jpg";
import eq2 from "../../assets/eq2.jpg";
import eq3 from "../../assets/eq3.webp";
import eq4 from "../../assets/eq4.webp";

const equipment = [
  {
    name: "Zemits DermeLuxx Pro",
    category: "Sistem Hidrodermabrazion",
    desc: "Platformë hidrodermabrazion me nivel mjekësor që ofron njëkohësisht eksfolim, pastrim të thellë të poreve dhe infuzion të lëkurës. Standardi i artë në rinovimin jo-invaziv të lëkurës.",
    features: [
      "Protokoll me shumë hapa",
      "Aqua peeling",
      "Infuzion serumi",
      "Eksfolim me majë diamanti",
    ],
    badge: "Flagship Device",
    img: eq1,
    bg: "#2C1F1A",
  },
  {
    name: "Dr. Pen A11 Ultima PRO",
    category: "Stilolaps Mikroneedling",
    desc: "Pajisje profesionale mikroneedling me thellësi të rregullueshme gjilpëre (0.25–3.0mm) dhe shpejtësi të ndryshueshme — duke ofruar induktim preciz të kolagjenit me shqetësim minimal.",
    features: [
      "Kasetë me 16 gjilpëra",
      "Motor 6000 RPM",
      "Thellësi e rregullueshme",
      "Teknologji chip inteligjente",
    ],
    badge: "Most Advanced",
    img: eq2,
    bg: "#1E1C1A",
  },
  {
    name: "RF Lifting System",
    category: "Pajisje Radiofrekuence",
    desc: "Teknologji radiofrekuence monopolare dhe bipolare që ngroh indin dermal për të stimuluar sintezën afatgjatë të kolagjenit — duke arritur ngritje klinike pa kirurgji.",
    features: [
      "Monopolare / Bipolare",
      "Temperaturë në kohë reale",
      "Sonde të shumëfishta",
      "E sigurt për të gjitha llojet e lëkurës",
    ],
    badge: "Clinical Grade",
    img: eq3,
    bg: "#231C18",
  },
  {
    name: "Ice Globe Therapy",
    category: "Masazh Cryo-Facial",
    desc: "Globet e ftohta të fytyrës rrëshqasin mbi lëkurë për të larguar ënjtjen menjëherë, shtrënguar poret, qetësuar skuqjen dhe rritur qarkullimin — duke lënë një shkëlqim të freskët dhe të skulpturuar pa kohë rikuperimi.",
    features: [
      "Heqje e menjëhershme e ënjtjes",
      "Shtrëngim i poreve",
      "Qetëson skuqjen",
      "Zero kohë rikuperimi",
    ],
    badge: "Cryo Glow",
    img: eq4,
    bg: "#1A1714",
  },
];

const Equipment = () => {
  return (
    <Section id="equipment">
      <Header>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <Label>Teknologjia Jonë</Label>
          <Title>
            Premium
            <br />
            <em>Equipment</em>
          </Title>
          <Sub>
            Investojmë vetëm në teknologjinë më të mirë klinike — sepse
            rezultatet tuaja varen nga mjetet që i përdorim.
          </Sub>
        </motion.div>
      </Header>

      <CardStack>
        {equipment.map((eq, i) => (
          <Card key={eq.name} $index={i} $bg={eq.bg}>
            <CardLeft>
              <CardNum>
                0{i + 1} / 0{equipment.length}
              </CardNum>
              <CardBadge>{eq.badge}</CardBadge>
              <CardCategory>{eq.category}</CardCategory>
              <CardName>{eq.name}</CardName>
              <CardDivider />
              <CardDesc>{eq.desc}</CardDesc>
              <FeatGrid>
                {eq.features.map((f) => (
                  <Feat key={f}>
                    <FeatDot />
                    {f}
                  </Feat>
                ))}
              </FeatGrid>
            </CardLeft>
            <CardRight>
              <CardImg src={eq.img} alt={eq.name} />
              <CardImgOverlay />
            </CardRight>
          </Card>
        ))}
      </CardStack>
    </Section>
  );
};

const Section = styled.section`
  background: var(--charcoal);
  position: relative;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 60px;
    right: 60px;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      rgba(196, 149, 122, 0.25),
      transparent
    );
  }
`;

const Header = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 140px 60px 100px;
  @media (max-width: 768px) {
    padding: 100px 24px 60px;
  }
`;

const Label = styled.span`
  display: block;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--rose);
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(48px, 6vw, 88px);
  font-weight: 300;
  color: var(--cream);
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: 0 0 24px;
  em {
    font-style: italic;
    color: var(--rose);
  }
`;

const Sub = styled.p`
  max-width: 500px;
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--taupe);
  line-height: 1.8;
`;

const CardStack = styled.div`
  /* Nothing special here — the stacking is all done by sticky on each Card */
`;

const Card = styled.div`
  position: sticky;
  top: ${({ $index }) => $index * 20}px;
  z-index: ${({ $index }) => $index + 1};

  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;
  background: #f0e3c297;
  border-top: 1px solid rgba(196, 149, 122, 0.12);
  overflow: hidden;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const CardLeft = styled.div`
  padding: 80px 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (max-width: 768px) {
    padding: 52px 24px;
  }
`;

const CardNum = styled.span`
  display: block;
  font-family: var(--font-display);
  font-size: 12px;
  color: rgba(196, 149, 122, 0.3);
  letter-spacing: 0.15em;
  margin-bottom: 20px;
`;

const CardBadge = styled.span`
  display: inline-block;
  align-self: flex-start;
  font-family: var(--font-body);
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--charcoal);
  background: var(--rose);
  padding: 5px 14px;
  margin-bottom: 28px;
`;

const CardCategory = styled.span`
  display: block;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--rose);
  margin-bottom: 12px;
`;

const CardName = styled.h3`
  font-family: var(--font-display);
  font-size: clamp(32px, 3.5vw, 54px);
  font-weight: 300;
  color: var(--cream);
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: 0;
`;

const CardDivider = styled.div`
  width: 40px;
  height: 1px;
  background: rgba(196, 149, 122, 0.3);
  margin: 28px 0;
`;

const CardDesc = styled.p`
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 300;
  color: var(--taupe);
  line-height: 1.85;
  margin: 0 0 36px;
  max-width: 460px;
`;

const FeatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
`;

const Feat = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.05em;
  color: var(--cream);
  opacity: 0.6;
`;

const FeatDot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--rose);
  flex-shrink: 0;
`;

const CardRight = styled.div`
  position: relative;
  overflow: hidden;
  @media (max-width: 900px) {
    height: 300px;
  }
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  filter: brightness(0.8) saturate(0.85);
  transition: transform 1s cubic-bezier(0.22, 1, 0.36, 1);
  ${CardRight}:hover & {
    transform: scale(1.04);
  }
`;

const CardImgOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to right,
    rgba(0, 0, 0, 0.25) 0%,
    transparent 60%
  );
  pointer-events: none;
`;

export default Equipment;
